import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, formatDate, todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Allowances() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "hr", "direktur"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", allowance_type_id: "", nama: "", nominal: 0, metode: "nominal_tetap", tanggal: todayISO(), keterangan: "" });
  const [del, setDel] = useState(null);

  const METODE = { per_hari: "Per Hari (× hari absen)", nominal_tetap: "Nominal Tetap (sekali)" };

  const load = () => api.get("/employee-allowances").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/allowance-types").then((r) => setTypes(r.data.filter((t) => t.kategori === "tunjangan")));
  }, []);
  const empName = (id) => employees.find((e) => e.id === id)?.nama || "-";

  const save = async () => {
    if (!form.employee_id || !form.nama) return toast.error("Pegawai & nama tunjangan wajib diisi");
    try { await api.post("/employee-allowances", { ...form, nominal: Number(form.nominal) }); toast.success("Tunjangan ditambahkan"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/employee-allowances/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "employee_id", label: "Pegawai", sortable: true, sortValue: (r) => empName(r.employee_id), render: (r) => empName(r.employee_id) },
    { key: "nama", label: "Tunjangan", sortable: true },
    { key: "tanggal", label: "Tanggal", render: (r) => r.tanggal ? formatDate(r.tanggal) : "-" },
    { key: "metode", label: "Metode", render: (r) => <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.metode === "per_hari" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{r.metode === "per_hari" ? "Per Hari" : "Nominal Tetap"}</span> },
    { key: "nominal", label: "Nominal", align: "right", render: (r) => <span>{rupiah(r.nominal)}{r.metode === "per_hari" && <span className="text-xs text-slate-400"> /hari</span>}</span> },
    { key: "keterangan", label: "Keterangan" },
    { key: "act", label: "Aksi", render: (r) => canWrite && <Button size="icon" variant="ghost" className="text-red-600" data-testid={`allow-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Tunjangan" subtitle="Tunjangan tambahan per pegawai (masuk perhitungan payroll)">
        {canWrite && <Button className="bg-blue-800 hover:bg-blue-900" data-testid="allow-add-btn" onClick={() => { setForm({ employee_id: "", allowance_type_id: "", nama: "", nominal: 0, metode: "nominal_tetap", tanggal: todayISO(), keterangan: "" }); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Tambah Tunjangan</Button>}
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["nama"]} testid="allow-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tambah Tunjangan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Pegawai *">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="allow-f-emp"><SelectValue placeholder="Pilih pegawai" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Jenis Tunjangan">
              <Select value={form.allowance_type_id || "custom"} onValueChange={(v) => { const t = types.find((x) => x.id === v); setForm({ ...form, allowance_type_id: v === "custom" ? "" : v, nama: t ? t.nama : form.nama, nominal: t?.nominal_default || form.nominal, metode: t?.metode === "per_hari" ? "per_hari" : (t ? "nominal_tetap" : form.metode) }); }}>
                <SelectTrigger><SelectValue placeholder="Pilih / custom" /></SelectTrigger>
                <SelectContent><SelectItem value="custom">- Custom -</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Nama Tunjangan *"><Input data-testid="allow-f-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></F>
            <F label="Nominal *"><Input type="number" data-testid="allow-f-nominal" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} /></F>
            <F label="Metode Perhitungan *">
              <Select value={form.metode} onValueChange={(v) => setForm({ ...form, metode: v })}>
                <SelectTrigger data-testid="allow-f-metode"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(METODE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              {form.metode === "per_hari" && <p className="mt-1 text-xs text-blue-600">Nominal akan dikalikan jumlah hari hadir pegawai pada periode payroll.</p>}
            </F>
            <F label="Tanggal"><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></F>
            <F label="Keterangan"><Textarea value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="allow-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="allow-delete-dialog"
        title="Hapus Tunjangan?" description="Yakin menghapus tunjangan ini?" confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
